import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Upload, Camera, Sparkles, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEMES = [
  { id: 'love', label: 'Vie amoureuse', icon: '💕' },
  { id: 'career', label: 'Carrière', icon: '💼' },
  { id: 'health', label: 'Santé', icon: '⚕️' },
  { id: 'longevity', label: 'Longévité', icon: '⏳' },
  { id: 'finances', label: 'Finances', icon: '💰' },
  { id: 'creativity', label: 'Créativité', icon: '🎨' },
  { id: 'spirituality', label: 'Spiritualité', icon: '✨' },
];

const HAND_STEPS = [
  { id: 'left-palm', label: 'Paume gauche', shortLabel: 'Paume G', icon: '🤚', description: 'Potentiel inné, dons naturels' },
  { id: 'right-palm', label: 'Paume droite', shortLabel: 'Paume D', icon: '🤚', description: 'Évolution, choix personnels' },
  { id: 'left-back', label: 'Dos gauche', shortLabel: 'Dos G', icon: '✋', description: 'Aspect physique, texture, veines' },
  { id: 'right-back', label: 'Dos droit', shortLabel: 'Dos D', icon: '✋', description: 'Aspect physique, texture, veines' },
];

const ScanPage = ({ sessionId }) => {
  const navigate = useNavigate();
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [leftPalmImage, setLeftPalmImage] = useState(null);
  const [rightPalmImage, setRightPalmImage] = useState(null);
  const [leftBackImage, setLeftBackImage] = useState(null);
  const [rightBackImage, setRightBackImage] = useState(null);
  const [activeHand, setActiveHand] = useState('left-palm');
  const [showWebcam, setShowWebcam] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Configuration caméra arrière (environment)
  const videoConstraints = {
    facingMode: { exact: "environment" }
  };

  const toggleTheme = (themeId) => {
    setSelectedThemes((prev) =>
      prev.includes(themeId) ? prev.filter((id) => id !== themeId) : [...prev, themeId]
    );
  };

  const getImageForHand = (handId) => {
    switch(handId) {
      case 'left-palm': return leftPalmImage;
      case 'right-palm': return rightPalmImage;
      case 'left-back': return leftBackImage;
      case 'right-back': return rightBackImage;
      default: return null;
    }
  };

  const setImageForHand = (handId, file) => {
    switch(handId) {
      case 'left-palm': setLeftPalmImage(file); break;
      case 'right-palm': setRightPalmImage(file); break;
      case 'left-back': setLeftBackImage(file); break;
      case 'right-back': setRightBackImage(file); break;
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image valide');
        return;
      }
      setImageForHand(activeHand, file);
      setShowWebcam(false);
      toast.success(`Image capturée avec succès`);
      
      // Auto-advance to next step if available
      autoAdvanceToNext();
    }
  };

  const captureFromWebcam = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      fetch(imageSrc)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], `${activeHand}-capture.jpg`, { type: 'image/jpeg' });
          setImageForHand(activeHand, file);
          setShowWebcam(false);
          toast.success(`Photo capturée avec succès`);
          
          // Auto-advance to next step if available
          autoAdvanceToNext();
        });
    }
  };

  const autoAdvanceToNext = () => {
    const currentIndex = HAND_STEPS.findIndex(s => s.id === activeHand);
    if (currentIndex < HAND_STEPS.length - 1) {
      const nextStep = HAND_STEPS[currentIndex + 1];
      if (!getImageForHand(nextStep.id)) {
        setTimeout(() => setActiveHand(nextStep.id), 500);
      }
    }
  };

  const getCurrentImage = () => getImageForHand(activeHand);

  const clearCurrentImage = () => setImageForHand(activeHand, null);

  const capturedCount = [leftPalmImage, rightPalmImage, leftBackImage, rightBackImage].filter(Boolean).length;
  const allImagesCaptured = capturedCount === 4;

  const analyzeImage = async () => {
    if (!allImagesCaptured) {
      toast.error('Veuillez capturer les 4 images (paume et dos des deux mains)');
      return;
    }
    
    if (selectedThemes.length === 0) {
      toast.error('Veuillez sélectionner au moins un thème');
      return;
    }

    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      
      formData.append('left_palm', leftPalmImage);
      formData.append('right_palm', rightPalmImage);
      formData.append('left_back', leftBackImage);
      formData.append('right_back', rightBackImage);
      
      formData.append('session_id', sessionId);
      formData.append('selected_themes', JSON.stringify(selectedThemes));
      formData.append('analysis_mode', 'full');

      const response = await axios.post(`${API}/readings/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      toast.success('Analyse terminée !');
      navigate(`/results/${response.data.id}`, { state: { reading: response.data } });
    } catch (error) {
      console.error('Error analyzing:', error);
      toast.error('Erreur lors de l\'analyse. Veuillez réessayer.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const currentStep = HAND_STEPS.find(s => s.id === activeHand);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 rounded-full hover:bg-secondary/50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4">
            Lecture de votre main
          </h1>
          <p className="text-muted-foreground text-base md:text-lg">
            Capturez les 4 photos de vos mains (paume et dos) pour une analyse complète
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progression des photos</span>
            <span className="text-sm text-muted-foreground">{capturedCount}/4</span>
          </div>
          <div className="h-2 bg-secondary/30 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-primary rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(capturedCount / 4) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Capture Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-card p-6 rounded-2xl border border-border shadow-sm">
              <h2 className="font-heading text-xl mb-4">1. Capturez vos 4 photos</h2>
              
              {/* Hand/Side Selection Tabs */}
              <div className="grid grid-cols-4 gap-2 mb-6">
                {HAND_STEPS.map((step, index) => {
                  const hasImage = getImageForHand(step.id);
                  const isActive = activeHand === step.id;
                  const isPalm = step.id.includes('palm');
                  
                  return (
                    <button
                      key={step.id}
                      data-testid={`${step.id}-tab`}
                      onClick={() => setActiveHand(step.id)}
                      className={`relative py-3 px-2 rounded-xl font-medium text-xs transition-all ${
                        isActive
                          ? isPalm 
                            ? 'bg-primary text-primary-foreground shadow-md'
                            : 'bg-orange-500 text-white shadow-md'
                          : hasImage
                            ? 'bg-green-100 text-green-700 border-2 border-green-300'
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      <span className="block">{step.icon}</span>
                      <span className="block mt-1">{step.shortLabel}</span>
                      {hasImage && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <Check className="h-3 w-3 text-white" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Info about current selection */}
              <div className="bg-secondary/20 p-3 rounded-xl mb-4">
                <p className="text-sm">
                  <strong>{currentStep?.label} :</strong>{' '}
                  <span className="text-muted-foreground">{currentStep?.description}</span>
                </p>
              </div>

              {/* Scan Area */}
              <div
                data-testid="scan-area"
                className="relative bg-white border-2 border-dashed border-primary/30 rounded-3xl aspect-[3/4] flex items-center justify-center overflow-hidden mb-6"
              >
                {!getCurrentImage() && !showWebcam && (
                  <div className="text-center p-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/50 flex items-center justify-center">
                      <Sparkles className="h-8 w-8 text-primary" />
                    </div>
                    <p className="text-muted-foreground text-sm mb-2 font-semibold">
                      {currentStep?.label}
                    </p>
                    <p className="text-muted-foreground text-xs mb-6">
                      Prenez une photo ou uploadez une image
                    </p>
                    <div className="flex flex-col gap-3">
                      <Button
                        data-testid="upload-button"
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-full bg-primary hover:bg-primary/90"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Uploader
                      </Button>
                      <Button
                        data-testid="camera-button"
                        onClick={() => setShowWebcam(true)}
                        variant="outline"
                        className="rounded-full"
                      >
                        <Camera className="mr-2 h-4 w-4" />
                        Caméra
                      </Button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                )}

                {showWebcam && (
                  <div className="absolute inset-0">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      videoConstraints={videoConstraints}
                      className="w-full h-full object-cover"
                      onUserMediaError={() => {
                        toast.error('Impossible d\'accéder à la caméra arrière. Essayez d\'uploader une photo.');
                        setShowWebcam(false);
                      }}
                    />
                    <div className="absolute top-4 left-0 right-0 text-center">
                      <span className="bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                        📷 Caméra arrière
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                      <Button
                        data-testid="capture-button"
                        onClick={captureFromWebcam}
                        className="rounded-full bg-primary hover:bg-primary/90"
                      >
                        <Camera className="mr-2 h-4 w-4" />
                        Capturer
                      </Button>
                      <Button
                        data-testid="cancel-webcam-button"
                        onClick={() => setShowWebcam(false)}
                        variant="outline"
                        className="rounded-full bg-white/90"
                      >
                        <X className="mr-2 h-4 w-4" />
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}

                {getCurrentImage() && !showWebcam && (
                  <div className="absolute inset-0 group">
                    <img
                      src={URL.createObjectURL(getCurrentImage())}
                      alt={activeHand}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <Button
                        data-testid="change-image-button"
                        onClick={() => {
                          clearCurrentImage();
                          fileInputRef.current?.click();
                        }}
                        variant="secondary"
                        className="rounded-full"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Changer
                      </Button>
                      <Button
                        onClick={() => {
                          clearCurrentImage();
                          setShowWebcam(true);
                        }}
                        variant="secondary"
                        className="rounded-full"
                      >
                        <Camera className="mr-2 h-4 w-4" />
                        Reprendre
                      </Button>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        Capturé
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Progress info */}
              <div className={`p-4 rounded-xl border ${allImagesCaptured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                <p className={`text-sm ${allImagesCaptured ? 'text-green-900' : 'text-orange-900'}`}>
                  {allImagesCaptured ? (
                    <>✅ <strong>Toutes les photos sont prêtes !</strong> Vous pouvez lancer l'analyse.</>
                  ) : (
                    <>📸 <strong>Progression :</strong> {capturedCount}/4 images capturées</>
                  )}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Theme Selection */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-card p-6 rounded-2xl border border-border shadow-sm">
              <h2 className="font-heading text-xl mb-6">2. Choisissez vos thèmes</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Sélectionnez les aspects de votre vie que vous souhaitez explorer
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {THEMES.map((theme) => {
                  const isSelected = selectedThemes.includes(theme.id);
                  return (
                    <motion.button
                      key={theme.id}
                      data-testid={`theme-${theme.id}`}
                      onClick={() => toggleTheme(theme.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`px-5 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                        isSelected
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      <span className="mr-2">{theme.icon}</span>
                      {theme.label}
                    </motion.button>
                  );
                })}
              </div>

              <AnimatePresence>
                {selectedThemes.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-secondary/30 p-4 rounded-xl mb-6"
                  >
                    <p className="text-sm text-secondary-foreground">
                      <strong>{selectedThemes.length}</strong> thème(s) sélectionné(s)
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <Button
                data-testid="analyze-button"
                onClick={analyzeImage}
                disabled={isAnalyzing || selectedThemes.length === 0 || !allImagesCaptured}
                className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAnalyzing ? (
                  <>
                    <Sparkles className="mr-2 h-5 w-5 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Analyser mes mains
                  </>
                )}
              </Button>

              {!allImagesCaptured && (
                <p className="text-xs text-muted-foreground text-center mt-4">
                  Capturez les 4 photos pour activer l'analyse
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ScanPage;
